// SHADOW MARKET™ — Global cart drawer. Inter, orange CTAs, glass dark.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2, ShoppingBag, AlertTriangle } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { useShopI18n } from "../i18n/shop";
import { API, safeJson } from "../lib/api";
import { FREE_SHIPPING_THRESHOLD } from "../data/shop-products";
import { RBLPaymentButton } from "./market/RBLPaymentButton";

export const ShopCartDrawer = () => {
  const { items, isOpen, closeCart, removeItem, updateQty, compute } = useCart();
  const { isPro, user, token } = useAuth();
  const { lang } = useLanguage();
  const t = useShopI18n(lang);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const totals = compute(isPro);

  const handleCheckout = async () => {
    if (!items.length) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch(`${API}/shadow-market/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          items: items.map((i) => ({
            productId: i.id,
            type: i.type,
            name: i.name,
            color: i.color,
            size: i.size,
            quantity: i.quantity,
            customization: i.customization
              ? {
                  base_color: i.customization.baseColor || null,
                  text: i.customization.text || null,
                  text_color: i.customization.textColor || null,
                  image_data_url: i.customization.imageDataUrl || null,
                  preview_data_url: i.customization.previewDataUrl || null,
                }
              : null,
          })),
          origin_url: window.location.origin,
          is_pro: !!isPro,
          user_email: user?.email || null,
          user_id: user?.id || null,
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      const data = await safeJson(res);
      if (data.url) window.location.href = data.url;
      else throw new Error("Stripe URL missing");
    } catch (e) {
      setErr(e.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className={`shop-overlay${isOpen ? " open" : ""}`}
        onClick={closeCart}
        data-testid="shop-cart-overlay"
      />
      <aside
        className={`shop-drawer${isOpen ? " open" : ""} fixed right-0 top-0 z-[9000] flex h-full w-full flex-col border-l border-white/10 bg-black/95 text-white backdrop-blur-2xl sm:w-[400px]`}
        data-testid="shop-cart-drawer"
        aria-hidden={!isOpen}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-4 w-4 text-orange-400" />
            <span className="text-sm font-black tracking-tight text-white">{t.cart_title}</span>
            <span className="text-xs font-bold text-white/40">({totals.itemCount})</span>
          </div>
          <button
            onClick={closeCart}
            className="rounded-full border border-white/10 bg-white/[0.04] p-1.5 text-white/60 transition-colors hover:border-white/30 hover:text-white"
            aria-label={t.cart_title}
            data-testid="shop-cart-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full border border-white/10 bg-white/[0.03]">
                <ShoppingBag className="h-5 w-5 text-white/40" />
              </div>
              <p className="text-sm text-white/55">{t.cart_empty}</p>
              <button
                onClick={() => { closeCart(); navigate("/shadow-market"); }}
                className="mt-5 text-[11px] font-black uppercase tracking-[0.22em] text-orange-300 transition-colors hover:text-orange-200"
                style={{ borderBottom: "1px solid currentColor", paddingBottom: 3 }}
                data-testid="shop-cart-empty-cta"
              >
                {t.cart_empty_cta} →
              </button>
            </div>
          ) : (
            <ul className="space-y-3">
              {items.map((it) => {
                const baseUnit = isPro ? it.proPrice : it.price;
                const customExtra = it.customization?.surcharge || 0;
                const lineUnit = baseUnit + customExtra;
                const customBg = it.customization?.baseColor || it.colorHex;
                return (
                  <li
                    key={it.key}
                    className="flex gap-3 rounded-xl border border-white/[0.08] bg-white/[0.02] p-3 backdrop-blur-sm"
                    data-testid={`shop-cart-line-${it.key}`}
                  >
                    {it.customization?.previewDataUrl ? (
                      <img
                        src={it.customization.previewDataUrl}
                        alt="preview"
                        className="h-14 w-14 shrink-0 rounded-lg object-cover"
                        style={{ boxShadow: "0 0 0 1px rgba(255,255,255,0.1), 0 6px 16px rgba(0,0,0,0.5)" }}
                      />
                    ) : (
                      <div
                        className="h-14 w-14 shrink-0 rounded-lg"
                        style={{
                          background: customBg,
                          boxShadow: "0 0 0 1px rgba(255,255,255,0.1), 0 6px 16px rgba(0,0,0,0.5)",
                        }}
                      />
                    )}
                    <div className="flex flex-1 flex-col gap-1.5">
                      <div className="flex items-center gap-1.5 text-xs font-black tracking-tight text-white">
                        <span>{it.name}</span>
                        {it.customization && (
                          <span className="studio-customize-badge" data-testid={`shop-cart-custom-${it.key}`}>
                            {t.cart_customized}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] tracking-[0.05em] text-white/45">
                        {it.customization?.baseColorLabel || it.color} · {t.product_size} {it.size}
                      </div>
                      {it.customization && (
                        <div className="text-[10px] text-orange-300/90">
                          {it.customization.text && <span>“{it.customization.text}” · </span>}
                          {t.cart_customization_extra}
                        </div>
                      )}
                      <div className="mt-1 flex items-center gap-3">
                        <div className="inline-flex items-center overflow-hidden rounded-full border border-white/10">
                          <button onClick={() => updateQty(it.key, -1)} className="px-2 py-1 text-white/55 transition-colors hover:text-white" data-testid={`shop-cart-dec-${it.key}`}>
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="min-w-[24px] text-center text-xs font-bold text-white">
                            {it.quantity}
                          </span>
                          <button onClick={() => updateQty(it.key, 1)} className="px-2 py-1 text-white/55 transition-colors hover:text-white" data-testid={`shop-cart-inc-${it.key}`}>
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <span className="text-sm font-black text-white">
                          €{(lineUnit * it.quantity).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => removeItem(it.key)}
                      className="self-start p-1 text-white/40 transition-colors hover:text-rose-400"
                      aria-label="Remove"
                      data-testid={`shop-cart-remove-${it.key}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Summary + checkout */}
        {items.length > 0 && (
          <div className="space-y-3 border-t border-white/10 bg-gradient-to-b from-white/[0.02] to-transparent px-5 py-5">
            <div className="space-y-1.5 text-xs">
              <Row label={t.cart_subtotal} value={`€${totals.subtotal.toFixed(2)}`} />
              {isPro && totals.discount > 0 && (
                <Row label={t.cart_discount} value={`-€${totals.discount.toFixed(2)}`} valueClass="text-orange-300" />
              )}
              <Row
                label={t.cart_shipping}
                value={totals.shipping === 0 ? t.cart_free : `€${totals.shipping.toFixed(2)}`}
                valueClass={totals.shipping === 0 ? "text-emerald-300" : ""}
              />
              {totals.subtotal < FREE_SHIPPING_THRESHOLD && totals.subtotal > 0 && (
                <p className="text-[10px] text-white/40">
                  {t.cart_add_for_free_shipping(FREE_SHIPPING_THRESHOLD - totals.subtotal)}
                </p>
              )}
              <div className="mt-3 flex items-center justify-between border-t border-dashed border-white/10 pt-3">
                <span className="text-[11px] font-black uppercase tracking-[0.22em] text-white/55">{t.cart_total}</span>
                <span className="text-xl font-black text-white">€{totals.total.toFixed(2)}</span>
              </div>
            </div>

            {err && (
              <div
                className="flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-500/[0.08] p-2 text-[11px] text-rose-200"
                data-testid="shop-cart-error"
              >
                <AlertTriangle className="h-3 w-3 shrink-0" /> <span>{err}</span>
              </div>
            )}

            <button
              onClick={handleCheckout}
              disabled={loading}
              className="shop-btn-primary w-full"
              style={{ height: 50, fontSize: "0.78rem", letterSpacing: "0.1em" }}
              data-testid="shop-cart-checkout"
            >
              {loading ? t.cart_redirecting : t.cart_checkout_with_total(totals.total)}
            </button>
            <RBLPaymentButton />
            <p className="text-center text-[9px] font-bold uppercase tracking-[0.22em] text-white/35">
              {t.cart_secure}
            </p>
          </div>
        )}
      </aside>
    </>
  );
};

const Row = ({ label, value, valueClass = "" }) => (
  <div className="flex items-center justify-between">
    <span className="text-white/45">{label}</span>
    <span className={`text-white ${valueClass}`}>{value}</span>
  </div>
);

export default ShopCartDrawer;
